import React, { useState } from 'react';
import { Modal, Button, Typography, Form, Upload, message } from 'antd';
import { Upload as UploadIcon, X, File, ShieldCheck } from 'lucide-react';

const { Text } = Typography;
const { Dragger } = Upload;

const DocumentModalV2 = ({ isOpen, onClose, record }) => {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload');
      return;
    }
    setLoading(true);
    try {
      console.log('Uploading Documents:', fileList);
      message.success('Documents uploaded successfully');
      onClose();
    } catch (error) {
      console.error('Failed to upload:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([...fileList, file]);
      return false;
    },
    fileList,
    multiple: true,
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <UploadIcon size={20} className="text-purple-600" />
          <span className="text-lg font-bold text-slate-800">Upload Document</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={550}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <div className="py-4 space-y-6">
        <div className="p-4 bg-purple-50 border border-purple-100 rounded-2xl flex items-start gap-3">
          <ShieldCheck size={20} className="text-purple-600 mt-1" />
          <div>
            <Text className="text-purple-900 font-bold block">Secure Upload</Text>
            <Text className="text-purple-700 text-xs">Files will be attached to {record?.PersonName || 'this record'} and visible to authorized users only.</Text>
          </div>
        </div>

        <Dragger {...uploadProps} className="bg-slate-50 border-2 border-dashed border-slate-200 hover:border-purple-400 hover:bg-white transition-all rounded-3xl p-10">
          <p className="flex justify-center mb-4">
            <div className="p-4 bg-purple-100 rounded-full text-purple-600">
              <UploadIcon size={32} />
            </div>
          </p>
          <p className="text-lg font-bold text-slate-700">Click or drag files to this area</p>
          <p className="text-slate-400 text-sm mt-2">Support for PDF, DOCX, JPG, PNG (Max 10MB)</p>
        </Dragger>

        {fileList.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto px-2">
            {fileList.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
                <div className="flex items-center gap-3">
                  <File size={16} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600 truncate max-w-[300px]">{file.name}</span>
                </div>
                <Text type="secondary" className="text-[10px] uppercase font-bold">{(file.size / 1024).toFixed(1)} KB</Text>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Button onClick={onClose} className="rounded-xl font-bold h-11 px-6">
            Cancel
          </Button>
          <Button 
            type="primary" 
            onClick={handleUpload}
            loading={loading}
            icon={<UploadIcon size={16} />}
            className="rounded-xl font-bold h-11 px-10 bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-200 border-none flex items-center gap-2"
          >
            Start Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentModalV2;
